/**
 * Settings Hook
 * 
 * Manages application settings stored in localStorage.
 */

import { useState, useCallback, useEffect } from 'react';
import type { Settings } from '../types';
import { setApiKey, clearApiKey } from '../api/client';

const SETTINGS_KEY = 'danny_settings';

const defaultSettings: Settings = {
  apiKey: '',
  theme: 'system',
  devMode: false,
  apiUrlOverride: undefined,
  transcriptionMethod: 'local-tiny',
  transcriptionVocabulary: '',
};

function loadSettings(): Settings {
  try {
    const stored = localStorage.getItem(SETTINGS_KEY);
    const parsed = stored ? JSON.parse(stored) : {};
    
    // ALWAYS check for API key in the legacy location (used by fetchApi)
    const legacyKey = localStorage.getItem('danny_api_key');
    
    return {
      ...defaultSettings,
      ...parsed,
      // Prefer legacy location for API key (this is where devConnect stores it)
      apiKey: legacyKey || parsed.apiKey || '',
    };
  } catch (e) {
    console.error('Failed to load settings:', e);
  }
  return defaultSettings;
}

function saveSettings(settings: Settings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    // Also update the API key in the legacy location for the client
    if (settings.apiKey) {
      setApiKey(settings.apiKey);
    } else {
      clearApiKey();
    }
  } catch (e) {
    console.error('Failed to save settings:', e);
  }
}

/**
 * Apply theme to document
 */
function applyTheme(theme: Settings['theme']) {
  const root = document.documentElement;

  if (theme === 'system') {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    root.classList.toggle('dark', prefersDark);
  } else {
    root.classList.toggle('dark', theme === 'dark');
  }
}

export function useSettings() {
  const [settings, setSettings] = useState<Settings>(loadSettings);

  // Sync settings changes to localStorage
  useEffect(() => {
    saveSettings(settings);
  }, [settings]);

  // Apply theme on mount and when it changes
  useEffect(() => {
    applyTheme(settings.theme);
  }, [settings.theme]);

  // Listen for system theme changes when theme is 'system'
  useEffect(() => {
    if (settings.theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyTheme('system');

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [settings.theme]);

  const updateApiKey = useCallback((apiKey: string) => {
    setSettings((prev) => ({ ...prev, apiKey }));
  }, []);

  const updateTheme = useCallback((theme: Settings['theme']) => {
    setSettings((prev) => ({ ...prev, theme }));
  }, []);

  const toggleDevMode = useCallback(() => {
    setSettings((prev) => ({ ...prev, devMode: !prev.devMode }));
  }, []);

  const setApiUrlOverride = useCallback((url: string | undefined) => {
    setSettings((prev) => ({ ...prev, apiUrlOverride: url }));

    // Also update localStorage directly for API client
    if (url) {
      localStorage.setItem('danny_api_url_override', url);
    } else {
      localStorage.removeItem('danny_api_url_override');
    }
  }, []);

  const clearCache = useCallback(() => {
    // Clear app data but keep API key
    const keysToKeep = ['danny_api_key', 'danny_api_key_confirmed', SETTINGS_KEY, 'danny_chat_history'];
    const allKeys = Object.keys(localStorage);

    allKeys.forEach(key => {
      if (key.startsWith('danny_') && !keysToKeep.includes(key)) {
        localStorage.removeItem(key);
      }
    });

    // Reload to clear in-memory caches
    window.location.reload();
  }, []);

  const clearSettings = useCallback(() => {
    setSettings(defaultSettings);
    clearApiKey();
    localStorage.removeItem(SETTINGS_KEY);
  }, []);

  const updateTranscriptionMethod = useCallback((method: Settings['transcriptionMethod']) => {
    setSettings((prev) => ({ ...prev, transcriptionMethod: method }));
  }, []);

  const updateTranscriptionVocabulary = useCallback((vocabulary: string) => {
    setSettings((prev) => ({ ...prev, transcriptionVocabulary: vocabulary }));
  }, []);

  return {
    settings,
    updateApiKey,
    updateTheme,
    toggleDevMode,
    setApiUrlOverride,
    clearCache,
    clearSettings,
    updateTranscriptionMethod,
    updateTranscriptionVocabulary,
  };
}

